/**
 * RebookPickerFilters — the rebook picker's fixed filter row (BON-146).
 *
 * The same month nav, view toggle and tree chips as the create-booking row, but
 * REGION IS A LABEL, NOT A CONTROL: every target is confined to the booking's
 * own region server-side, so a dropdown would offer exactly one option and imply
 * there were others. Saying which region it is, once, is the honest version.
 */
import { MapPin } from 'lucide-react';

import { FilterChipRow } from './FilterChipRow';
import type { PickerViewMode } from './pickerViewMode';
import { ViewModeToggle } from './ViewModeToggle';
import type { FilterOption } from './WorkshopPickerHelpers';
import { WorkshopPickerMonthNav } from './WorkshopPickerMonthNav';

export interface RebookPickerFiltersProps {
  /** The booking's region. Null when the workshop has none on record. */
  regionName: string | null;
  treeOptions: FilterOption[];
  treeSpeciesId: string | null;
  onTreeChange: (id: string | null) => void;
  viewMode: PickerViewMode;
  onViewModeChange: (mode: PickerViewMode) => void;
  month: Date;
  onMonthChange: (month: Date) => void;
}

export function RebookPickerFilters({
  regionName,
  treeOptions,
  treeSpeciesId,
  onTreeChange,
  viewMode,
  onViewModeChange,
  month,
  onMonthChange,
}: RebookPickerFiltersProps) {
  return (
    <div className="space-y-3 border-b border-stone-200 px-5 py-3 dark:border-stone-700">
      {viewMode === 'calendar' && (
        <WorkshopPickerMonthNav month={month} onMonthChange={onMonthChange} />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-400">
            Region
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-bark-900 dark:text-stone-100">
            <MapPin size={14} className="flex-shrink-0 text-stone-400" />
            {regionName ?? 'This region'}
            <span className="font-normal text-xs text-stone-500">
              &middot; a booking can only move within its own region
            </span>
          </span>
        </div>
        <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
      </div>
      <FilterChipRow
        ariaLabel="Filter by tree type"
        allLabel="All trees"
        options={treeOptions}
        activeId={treeSpeciesId}
        onChange={onTreeChange}
      />
    </div>
  );
}
